import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';

/**
 * @description
 * Restricts an input element's value based on a regular expression.
 *
 * A custom regex pattern can be provided via the `appInputRegex` input. If no pattern is provided,
 * or if the provided pattern is invalid, it defaults to allowing alphanumeric characters and
 * single spaces.
 *
 * The directive handles user typing, pasting, and drop events to enforce the pattern and
 * prevent invalid characters or consecutive spaces.
 *
 * @usage
 * * <input type="text" appInputRegex>
 *
 * * <input type="text" [appInputRegex]="'^[0-9]*$'">
 */
@Directive({
  selector: '[appInputRegex]',
  standalone: true,
})
export class InputRegexDirective implements OnInit, OnChanges {
  @Input('appInputRegex') regexPattern: string = '';

  private readonly defaultRegex = /^[a-zA-Z0-9\s]*$/;

  private compiledRegex: RegExp = this.defaultRegex;

  constructor(private readonly el: ElementRef) {}

  ngOnInit() {
    this.updateRegex();
  }

  ngOnChanges() {
    this.updateRegex();
  }

  private updateRegex(): void {
    if (this.regexPattern?.trim()) {
      try {
        const cleanPattern = this.regexPattern.replace(/(^\/)|(\/$)/g, '');
        this.compiledRegex = new RegExp(cleanPattern, 'u');
      } catch (error) {
        console.warn('Regex inválido fornecido, usando padrão default:', error);
        this.compiledRegex = this.defaultRegex;
      }
    } else {
      this.compiledRegex = this.defaultRegex;
    }
  }

  private isValidInput(text: string): boolean {
    return this.compiledRegex.test(text);
  }

  private removeDoubleSpaces(text: string): string {
    return text.replace(/\s{2,}/g, ' ');
  }

  private filterValidChars(text: string): string {
    if (!text) return '';

    let filteredText = text.trimStart().replace(/\s{2,}/g, ' ');

    if (this.isUsingDefaultRegex()) {
      const invalidCharsRegex = /[^a-zA-Z0-9\s]/g;
      filteredText = filteredText.replace(invalidCharsRegex, '');
    } else if (!this.isValidInput(filteredText)) {
      return this.el.nativeElement.value;
    }

    return this.removeDoubleSpaces(filteredText);
  }

  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    const char = event.key;

    if (event.ctrlKey || event.metaKey || char.length > 1) {
      return;
    }

    const currentValue = this.el.nativeElement.value;
    const selectionStart = this.el.nativeElement.selectionStart || 0;
    const selectionEnd = this.el.nativeElement.selectionEnd || 0;

    if (char === ' ') {
      const charBeforeCursor =
        selectionStart > 0 ? currentValue[selectionStart - 1] : '';
      if (charBeforeCursor === ' ') {
        event.preventDefault();
        return;
      }
    }

    if (this.isUsingDefaultRegex()) {
      if (!/[a-zA-Z0-9\s]/.test(char)) {
        event.preventDefault();
        return;
      }
    } else {
      const newValue =
        currentValue.substring(0, selectionStart) +
        char +
        currentValue.substring(selectionEnd);

      if (!this.isValidInput(newValue)) {
        event.preventDefault();
        return;
      }
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: InputEvent): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;

    const filteredValue = this.filterValidChars(currentValue);

    if (filteredValue !== currentValue) {
      const cursorPosition = input.selectionStart || 0;
      input.value = filteredValue;

      const newCursorPosition = Math.min(cursorPosition, filteredValue.length);
      input.setSelectionRange(newCursorPosition, newCursorPosition);

      const newEvent = new Event('input', { bubbles: true });
      setTimeout(() => input.dispatchEvent(newEvent), 0);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const pastedText = clipboardData.getData('text');
    const input = this.el.nativeElement;
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    const cleanPastedText = this.removeDoubleSpaces(pastedText);

    let filteredPastedText = '';

    if (this.isUsingDefaultRegex()) {
      filteredPastedText = cleanPastedText
        .split('')
        .filter((char) => {
          return /[a-zA-Z0-9\s]/.test(char);
        })
        .join('');
    } else {
      filteredPastedText = cleanPastedText;
    }

    filteredPastedText = this.removeDoubleSpaces(filteredPastedText);

    const newValue =
      currentValue.substring(0, selectionStart) +
      filteredPastedText +
      currentValue.substring(selectionEnd);

    const finalValue = this.removeDoubleSpaces(newValue);

    if (this.isValidInput(finalValue)) {
      input.value = finalValue;

      const newCursorPosition = selectionStart + filteredPastedText.length;
      input.setSelectionRange(newCursorPosition, newCursorPosition);

      setTimeout(
        () => input.dispatchEvent(new Event('input', { bubbles: true })),
        0
      );
    }
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();

    const droppedText = event.dataTransfer?.getData('text');
    if (!droppedText) return;

    const input = this.el.nativeElement;
    const cleanText = this.removeDoubleSpaces(droppedText);
    const filteredText = this.filterValidChars(cleanText);

    if (filteredText && this.isValidInput(input.value + filteredText)) {
      const currentValue = input.value;
      const cursorPosition = input.selectionStart || 0;

      const newValue =
        currentValue.substring(0, cursorPosition) +
        filteredText +
        currentValue.substring(cursorPosition);

      const finalValue = this.removeDoubleSpaces(newValue);
      input.value = finalValue;
      setTimeout(
        () => input.dispatchEvent(new Event('input', { bubbles: true })),
        0
      );
    }
  }

  private isUsingDefaultRegex(): boolean {
    return !this.regexPattern?.trim();
  }
}
